import { SearchConversationsParams } from '@/api/conversations';
import {type GenerateImageParams} from '@/api/images';
import {type GenerateVideoRequest} from '@/api/videos';

type ConversationSearchFilters = Omit<SearchConversationsParams, 'limit' | 'offset'>;

export const queryKeys = {
	conversations: {
		all: ['conversations'] as const,
		list: () => ['conversations', 'list'] as const,
		detail: (id: string) => ['conversations', 'detail', id] as const,
		messages: (conversationId: string) =>
			['conversations', 'messages', conversationId] as const,
		searchRoot: () => ['conversations', 'search'] as const,
		search: (filters: ConversationSearchFilters) =>
			['conversations', 'search', filters] as const,
	},
	userSettings: {
		all: ['userSettings'] as const,
		current: () => ['userSettings', 'current'] as const,
		list: () => ['userSettings', 'list'] as const,
		detail: (userId: string) => ['userSettings', 'detail', userId] as const,
		myModelsUsage: (startDate: string, endDate: string, limit: number = 5) => ['userSettings', 'myModelsUsage', startDate, endDate, limit] as const,
		myTotalMessages: (startDate: string, endDate: string) => ['userSettings', 'myTotalMessages', startDate, endDate] as const,
		myTotalCosts: (startDate: string, endDate: string) => ['userSettings', 'myTotalCosts', startDate, endDate] as const,
		myMonthlyCosts: (numMonths: number = 6) => ['userSettings', 'myMonthlyCosts', numMonths] as const,
	},
	permissionRequests: {
		all: ['permissionRequests'] as const,
		list: (status?: 'pending' | 'approved' | 'denied' | 'cancelled', groupBy?: 'user') => ['permissionRequests', 'list', status, groupBy] as const,
		detail: (requestId: string) => ['permissionRequests','detail', requestId] as const,
		accessible: () => ['permissionRequests', 'accessible'] as const,
		requestable: () => ['permissionRequests', 'requestable'] as const,
	},
	admin: {
		all: ['admin'] as const,
		models: {
			all: ['admin', 'models'] as const,
			list: () => ['admin', 'models', 'list'] as const,
		},
		agents: {
			all: ['admin', 'agents'] as const,
			list: () => ['admin', 'agents', 'list'] as const,
		},
		features: {
			all: ['admin', 'features'] as const,
			list: () => ['admin', 'features', 'list'] as const,
		},
	},
	dashboard: {
		all: ['dashboard'] as const,
		mostActiveUsers: (startDate: string, endDate: string, limit: number = 5) => ['dashboard', 'mostActiveUsers', startDate, endDate, limit] as const,
		messageTotals: (startDate: string, endDate: string) => ['dashboard','messageTotals', startDate, endDate] as const,
		modelUsage: (startDate: string, endDate: string, limit: number = 5) => ['dashboard','modelUsage', startDate, endDate, limit] as const,
		usersWithMetrics: (startDate: string, endDate: string) => ['dashboard','usersWithMetrics', startDate, endDate] as const,
		userModelUsageDetails: (userId: string, startDate: string, endDate: string) => ['dashboard','userModelUsageDetails', userId, startDate, endDate] as const,
		monthlyTotalCosts: (requestingUserId: string, requestingUserRole: string, requestingUserIsAdmin: boolean, numMonths: number = 12) => ['dashboard','monthlyTotalCosts', requestingUserId, requestingUserRole, requestingUserIsAdmin, numMonths] as const,
		ttftByModel: (startDate: string, endDate: string) => ['dashboard', 'ttftByModel', startDate, endDate] as const,
		durationByAgent: (startDate: string, endDate: string) => ['dashboard', 'durationByAgent', startDate, endDate] as const,
		dailyMessageActivity: (startDate: string, endDate: string) => ['dashboard', 'dailyMessageActivity', startDate, endDate] as const,
	},
	images: {
		history: () => ['images', 'history'] as const,
		generateImage: (params: GenerateImageParams) => ['images', 'generateImage', params] as const,
	},
	videos: {
		history: () => ['videos', 'history'] as const,
		generateVideo: (request: GenerateVideoRequest) => ['videos', 'generateVideo', request] as const,
		deleteVideo: (videoId: string) => ['videos', 'deleteVideo', videoId] as const,
	}
};

export type QueryKeys = typeof queryKeys;
